import React, { useMemo, useState, useEffect } from 'react';
import { Box, Modal, Typography } from '@material-ui/core';
import { Chess } from 'chess.js';
import {
  Chessboard,
  ChessboardDnDProvider,
  SparePiece,

} from 'react-chessboard';
import { createMove } from '../../util/Chess';

const modalBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginLeft: 'max(-450px, -45%)',
  marginTop: 'max(-325px, -37%)',
  width: '90%',
  height: '80%',
  maxWidth: '900px', // Increased max width to accommodate the new layout
  maxHeight: '600px',

  bgcolor: 'rgba(255, 255, 255, 0.95)',
  border: '5px solid black',
  borderRadius: '10px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px'
};

const toggleTurnInFEN = (fen) => {
  const parts = fen.split(' ');
  if (parts[1] === 'w') {
    parts[1] = 'b';
  } else {
    parts[1] = 'w';
  }
  return parts.join(' ');
};

const FENBuilderModal = ({ fen: startFEN, onFENUpdate, onPGNUpdate, onClose }) => {
  const [modalOpen, setModalOpen] = useState(true);
  const [fen, setFen] = useState(startFEN || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
  const [position, setPosition] = useState(startFEN || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
  const [boardOrientation, setBoardOrientation] = useState('white');
  const [pgnInput, setPgnInput] = useState("");
  const [pgnError, setPgnError] = useState("");
  const [fenError, setFenError] = useState("");
  const [moveList, setMoveList] = useState([]);
  const [isPgnMode, setIsPgnMode] = useState(false); // Track if PGN is the active mode
  const [gameResult, setGameResult] = useState({
    result: null, // "checkmate", "draw", "unfinished"
    winner: null, // "white", "black", null (for draw/unfinished)
    reason: null  // Specific reason like "stalemate", "insufficient material", etc.
  });
  const [validationMessage, setValidationMessage] = useState({ text: "", isError: false });
  const chess = useMemo(() => new Chess(), []);

  // Initialize the chess instance with the starting FEN
  useEffect(() => {
    try {
      chess.load(fen);
      setPosition(chess.fen());
    } catch (e) {
      setFenError("Invalid FEN position");
    }
  }, []);

  // Effect to update isPgnMode whenever pgnInput changes
  useEffect(() => {
    setIsPgnMode(!!pgnInput.trim());
  }, [pgnInput]);

  // Effect to update board position when FEN changes
  useEffect(() => {
    try {
      if (fen && !isPgnMode) {
        chess.load(fen);
        setPosition(chess.fen());

        // Update board orientation based on whose turn it is in the FEN
        const fenParts = fen.split(' ');
        if (fenParts.length > 1) {
          const turn = fenParts[1];
          setBoardOrientation(turn === 'w' ? 'white' : 'black');
        }

        setFenError("");
      }
    } catch (e) {
      setFenError("Invalid FEN position");
    }
  }, [fen, isPgnMode]);

  const handleSparePieceDrop = (piece, targetSquare) => {
    // Prevent piece drops if in PGN mode
    if (isPgnMode) return false;

    const color = piece[0];
    const type = piece[1].toLowerCase();
    const success = chess.put(
      {
        type,
        color,
      },
      targetSquare,
    );
    if (success) {
      const newFen = chess.fen();
      setPosition(newFen);
      setFen(newFen);
    } else {
      alert(
        `The board already contains ${color === 'w' ? 'WHITE' : 'BLACK'} KING`,
      );
    }
    return success;
  };

  const handlePieceDrop = (sourceSquare, targetSquare, piece) => {
    // Prevent piece drops if in PGN mode
    if (isPgnMode) return false;

    const color = piece[0];
    const type = piece[1].toLowerCase();

    // this is hack to avoid chess.js bug, which I've fixed in the latest version https://github.com/jhlywa/chess.js/pull/426
    chess.remove(sourceSquare);
    chess.remove(targetSquare);
    const success = chess.put(
      {
        type,
        color,
      },
      targetSquare,
    );
    if (success) {
      const newFen = chess.fen();
      setPosition(newFen);
      setFen(newFen);
    }
    return success;
  };

  const handlePieceDropOffBoard = (sourceSquare) => {
    // Prevent piece drops if in PGN mode
    if (isPgnMode) return false;

    chess.remove(sourceSquare);
    const newFen = chess.fen();
    setPosition(newFen);
    setFen(newFen);
  };

  const handleFenInputChange = (e) => {
    if (isPgnMode) return; // Prevent FEN changes if in PGN mode
    setFen(e.target.value);
    setFenError("");
  };

  /**
   * Normalize chess move notation to standard case:
   * - Files (a-h): lowercase
   * - Pieces (NBRQK): uppercase  
   * - Special moves (O-O, O-O-O): uppercase
   */
  const normalizeChessMove = (move) => {
    if (!move) return move;
    
    // Handle castling - normalize to uppercase O's
    if (move.match(/^[o0]-[o0](-[o0])?[+#]?$/i)) {
      return move.replace(/[o0]/g, 'O');
    }
    
    // For regular moves, apply case rules:
    // 1. Piece letters (N,B,R,Q,K) should be uppercase
    // 2. File letters (a-h) should be lowercase  
    // 3. Promotion pieces should be uppercase
    
    let normalized = move;
    
    // Normalize piece notation at the start (Nf3, Bxc4, etc.)
    normalized = normalized.replace(/^([nbrqk])/i, (match, piece) => piece.toUpperCase());
    
    // Normalize file letters (a-h) to lowercase
    normalized = normalized.replace(/([A-H])([1-8])/g, (match, file, rank) => file.toLowerCase() + rank);
    
    // Normalize promotion notation (=Q, =R, =B, =N)
    normalized = normalized.replace(/=([nbrq])/gi, (match, piece) => '=' + piece.toUpperCase());
    
    return normalized;
  };

  const handlePgnInputChange = (e) => {
    setPgnInput(e.target.value);
    setPgnError("");

    // If PGN is being cleared, restore FEN as the active mode
    if (!e.target.value.trim()) {
      setIsPgnMode(false);
    } else {
      setIsPgnMode(true);
    }
  };

  // Handle flip board logic
  const handleFlipBoard = () => {
    // If in PGN mode, don't allow flipping
    if (isPgnMode) return;

    // Toggle board orientation
    const newOrientation = boardOrientation === 'white' ? 'black' : 'white';
    setBoardOrientation(newOrientation);

    // Update FEN to match - ensure the turn is of the side on the bottom
    const parts = position.split(' ');
    // If new orientation is white, turn should be 'w', if black, turn should be 'b'
    parts[1] = newOrientation === 'white' ? 'w' : 'b';
    const newFen = parts.join(' ');
    setPosition(newFen);
    setFen(newFen);
  };

  /**
   * Comprehensive PGN processor that creates moves identical to manual play
   * This ensures each move has proper structure including startSquare, endSquare, FEN, etc.
   * @param {string} pgnInput - The PGN string to process
   * @returns {Object} - Result object with success status and processed moves
   */
  const processComprehensivePGN = (pgnInput) => {
    
    

    try {
      // Create a fresh chess instance for processing
      const chess = new Chess();
      
      
      
      
      
      
      // Preprocess PGN string
      let processedPgn = pgnInput.replace(/\r?\n/g, ' ').trim();
      processedPgn = processedPgn.replace(/\s+/g, ' ');
      processedPgn = processedPgn.replace(/(\d+)\.([a-zA-Z0-9])/g, '$1. $2');

      

      // Try to load and validate PGN
      let isValidPGN = false;
      try {
        // Try the newer API first (loadPgn), then fall back to older API (load_pgn)
        if (typeof chess.loadPgn === 'function') {
          isValidPGN = chess.loadPgn(processedPgn, { sloppy: true });
        } else if (typeof chess.load_pgn === 'function') {
          isValidPGN = chess.load_pgn(processedPgn, { sloppy: true });
        } else {
          console.error('[FENBuilderModal] No PGN loading method found on chess instance');
          throw new Error('PGN loading not supported by chess.js version');
        }
        
      } catch (e) {
        console.warn('[FENBuilderModal] PGN validation failed:', e.message);
      }

      if (!isValidPGN) {
        console.error('[FENBuilderModal] processComprehensivePGN - PGN INVALID');
        
        
        // Try manual PGN parsing as fallback
        try {
          const chess = new Chess();
          const comprehensiveMoves = [];
          
          // Extract moves from PGN manually
          // Remove headers and comments
          let moveText = processedPgn.replace(/\[.*?\]/g, '').replace(/\{.*?\}/g, '').trim();
          
          
          
          // Extract individual moves using regex
          // This regex matches move notation like e4, Nf3, O-O, Qxh7+, etc.
          const movePattern = /([a-hA-H]?[1-8]?[NBRQK]?[a-hA-H][1-8](?:=[NBRQ])?[+#]?|O-O(?:-O)?[+#]?)/g;
          const moves = [];
          let match;
          
          while ((match = movePattern.exec(moveText)) !== null) {
            const move = match[1];
            // Skip game result indicators
            if (!['1-0', '0-1', '1/2-1/2', '*'].includes(move)) {
              moves.push(move);
            }
          }
          
          
          
          if (moves.length === 0) {
            console.error('[FENBuilderModal] No moves extracted from PGN');
            throw new Error('No moves could be extracted from PGN');
          }
          
          // Apply moves one by one
          for (let i = 0; i < moves.length; i++) {
            const moveNotation = moves[i];
            
            
            
            // Skip result indicators
            if (['1-0', '0-1', '1/2-1/2', '*'].includes(moveNotation)) {
              
              break;
            }
            
            const fenBeforeMove = chess.fen();
            
            
            try {
              const normalizedMove = normalizeChessMove(moveNotation);
              const appliedMove = chess.move(normalizedMove, { sloppy: true });
              
              
              if (appliedMove) {
                const fenAfterMove = chess.fen();
                
                
                // Create comprehensive move object directly with all required properties
                const moveObject = {
                  notation: moveNotation,
                  from: appliedMove.from,
                  to: appliedMove.to,
                  startSquare: appliedMove.from,
                  endSquare: appliedMove.to,
                  fen: fenAfterMove,
                  fenBefore: fenBeforeMove,
                  piece: appliedMove.piece,
                  captured: appliedMove.captured || null,
                  promotion: appliedMove.promotion || null,
                  content: '',
                  arrows: [],
                  highlights: []
                };
                
                comprehensiveMoves.push(moveObject);
                
                
              } else {
                console.error(`[FENBuilderModal] Manual parsing failed at move: ${moveNotation}`);
                break;
              }
            } catch (e) {
              console.error(`[FENBuilderModal] Manual parsing error at move ${moveNotation}:`, e);
              break;
            }
          }
          
          if (comprehensiveMoves.length > 0) {
            
            
            // Analyze game result
            const gameResult = {
              result: "unfinished",
              winner: null,
              reason: "ongoing"
            };

            if (chess.game_over()) {
              if (chess.in_checkmate()) {
                gameResult.result = "checkmate";
                gameResult.winner = chess.turn() === 'w' ? 'black' : 'white';
                gameResult.reason = "checkmate";
              } else if (chess.in_draw()) {
                gameResult.result = "draw";
                if (chess.in_stalemate()) {
                  gameResult.reason = "stalemate";
                } else if (chess.insufficient_material()) {
                  gameResult.reason = "insufficient material";
                } else {
                  gameResult.reason = "fifty move rule or repetition";
                }
              }
            }
            
            
            
            return {
              success: true,
              moves: comprehensiveMoves,
              gameResult,
              finalFEN: chess.fen()
            };
          } else {
            console.error('[FENBuilderModal] Manual parsing failed - no moves were successfully applied');
          }
          
        } catch (manualError) {
          console.error('[FENBuilderModal] Manual parsing also failed:', manualError);
        }
        
        return {
          success: false,
          error: "Invalid PGN format",
          moves: []
        };
      }

      // Get the move history from chess.js
      const moveHistory = chess.history({ verbose: true });
      
      

      // Now rebuild moves step by step to match manual play structure
      const tempChess = new Chess();
      const comprehensiveMoves = [];

      for (let i = 0; i < moveHistory.length; i++) {
        const chessMove = moveHistory[i];
        
        
        
        // Get the FEN before this move
        const fenBeforeMove = tempChess.fen();
        
        // Apply the move to our temp chess instance with normalization
        const normalizedMove = normalizeChessMove(chessMove.san);
        const appliedMove = tempChess.move(normalizedMove, { sloppy: true });
        
        if (appliedMove) {
          // Create comprehensive move object directly with all required properties
          const moveObject = {
            notation: chessMove.san,
            from: appliedMove.from,
            to: appliedMove.to,
            startSquare: appliedMove.from,
            endSquare: appliedMove.to,
            fen: tempChess.fen(), // FEN after the move
            fenBefore: fenBeforeMove, // FEN before the move
            piece: appliedMove.piece,
            captured: appliedMove.captured || null,
            promotion: appliedMove.promotion || null,
            content: '',
            arrows: [],
            highlights: []
          };
          
          
          
          comprehensiveMoves.push(moveObject);
        } else {
          console.error(`[FENBuilderModal] Failed to apply move: ${chessMove.san}`);
          break;
        }
      }

      // Analyze game result
      const gameResult = {
        result: "unfinished",
        winner: null,
        reason: "ongoing"
      };

      if (chess.game_over()) {
        if (chess.in_checkmate()) {
          gameResult.result = "checkmate";
          gameResult.winner = chess.turn() === 'w' ? 'black' : 'white';
          gameResult.reason = "checkmate";
        } else if (chess.in_draw()) {
          gameResult.result = "draw";
          if (chess.in_stalemate()) {
            gameResult.reason = "stalemate";
          } else if (chess.insufficient_material()) {
            gameResult.reason = "insufficient material";
          } else {
            gameResult.reason = "fifty move rule or repetition";
          }
        }
      }

      // console.log('[FENBuilderModal] processComprehensivePGN - SUCCESS:', {
      //   movesCount: comprehensiveMoves.length,
      //   gameResult
      // });

      return {
        success: true,
        moves: comprehensiveMoves,
        gameResult,
        finalFEN: chess.fen()
      };

    } catch (error) {
      console.error('[FENBuilderModal] processComprehensivePGN - ERROR:', error);
      return {
        success: false,
        error: error.message || "Unknown error during PGN processing",
        moves: []
      };
    }
  };

  const handleLoadPgn = () => {
    
    
    try {
      // Reset errors
      setPgnError("");
      setValidationMessage({ text: "", isError: false });


      // If PGN is empty, show error
      if (!pgnInput.trim()) {
        const error = "Please enter a PGN";
        console.error("PGN parsing failed: Empty input");
        setPgnError(error);
        setValidationMessage({ text: error, isError: true });
        return false;
      }

      
      

      // Use the new comprehensive PGN processor
      const comprehensiveResult = processComprehensivePGN(pgnInput.trim());
      
      

      if (comprehensiveResult.success) {
        // console.log('[FENBuilderModal] Comprehensive PGN processing successful:', {
        //   movesCount: comprehensiveResult.moves.length,
        //   gameResult: comprehensiveResult.gameResult
        // });

        // Store the comprehensive result for later use
        chess.load(comprehensiveResult.finalFEN);
        setFen(comprehensiveResult.finalFEN);
        setPosition(comprehensiveResult.finalFEN);
        setGameResult(comprehensiveResult.gameResult);

        // Create moveList for display compatibility
        const moveList = comprehensiveResult.moves.map((move, index) => ({
          move: move.notation,
          content: move.content || '',
          fen: move.fen,
          moveNumber: Math.floor(index / 2) + 1,
          color: index % 2 === 0 ? 'white' : 'black'
        }));
        setMoveList(moveList);

        // Store comprehensive moves for retrieval
        window._lastComprehensiveResult = comprehensiveResult;
        
        

        return true;
      } else {
        // Fall back to original processing if comprehensive fails
        
        
        // Clear any stored comprehensive result
        window._lastComprehensiveResult = null;

        // Reset game result for fallback strategy
        setGameResult({
          result: "unknown",
          winner: null,
          reason: "pgn parsing failed"
        });

        try {
          // Extract just the moves, ignoring headers and comments
          const moveText = pgnInput.replace(/\[.*?\]/g, '').replace(/\{.*?\}/g, '');

          // Try to extract moves in a more flexible way
          // This regex looks for patterns like "1. e4" or "1...e5" or just "e4"
          const movePattern = /(?:\d+\.+\s*)?([a-zA-Z][a-zA-Z0-9\+\#\=\-]+)(?:\s+|$)/g;
          let match;
          let cleanMoves = [];

          while ((match = movePattern.exec(moveText)) !== null) {
            if (match[1]) cleanMoves.push(match[1]);
          }



          // If no moves were found, try a simpler approach - just split by spaces and filter
          if (cleanMoves.length === 0) {

            cleanMoves = moveText.split(/\s+/).filter(move =>
              /^[a-zA-Z][a-zA-Z0-9\+\#\=\-]+$/.test(move) &&
              !move.match(/^\d+\.+$/)
            );

          }

          // Reset and try to apply moves one by one
          chess.reset();
          let allMovesApplied = true;
          let lastSuccessfulFen = chess.fen();
          let appliedMoves = 0;
          let failedMoveIndex = -1;
          let failedMove = null;
          let failureReason = null;
          
          // Create proper moves array with all required properties
          const properMoves = [];

          for (let i = 0; i < cleanMoves.length; i++) {
            const move = cleanMoves[i];
            try {
              const fenBeforeMove = chess.fen();
              const normalizedMove = normalizeChessMove(move);
              const appliedMove = chess.move(normalizedMove, { sloppy: true });

              if (appliedMove) {
                // Create proper move object with all required properties
                const moveObject = {
                  notation: appliedMove.san,
                  from: appliedMove.from,
                  to: appliedMove.to,
                  startSquare: appliedMove.from,
                  endSquare: appliedMove.to,
                  fen: chess.fen(),
                  fenBefore: fenBeforeMove,
                  piece: appliedMove.piece,
                  captured: appliedMove.captured || null,
                  promotion: appliedMove.promotion || null,
                  content: '',
                  arrows: [],
                  highlights: []
                };
                
                properMoves.push(moveObject);
                lastSuccessfulFen = chess.fen();
                appliedMoves++;
                
                

              } else {
                allMovesApplied = false;
                failedMoveIndex = i;
                failedMove = move;
                failureReason = "Invalid move";
                console.error(`  Failed! Move ${move} is invalid in this position`);
                break;
              }
            } catch (e) {
              allMovesApplied = false;
              failedMoveIndex = i;
              failedMove = move;
              failureReason = e.message || "Exception during move";
              console.error(`  Exception! Move ${move} caused error:`, e.message || e);
              break;
            }
          }

          if (cleanMoves.length > 0) {
            // Even if not all moves were applied, use the last successful position
            setFen(lastSuccessfulFen);
            setPosition(lastSuccessfulFen);

            if (!allMovesApplied) {
              const errorMsg = `Partial success: ${appliedMoves} of ${cleanMoves.length} moves applied. Failed at move: "${failedMove}" (${failureReason})`;
              console.warn(errorMsg);
              setPgnError(errorMsg);
              setValidationMessage({ text: errorMsg, isError: true });
              return false;
            } else if (properMoves.length > 0) {
              // SUCCESS: Store the proper moves for comprehensive processing
              
              
              // Create moveList for display
              const moveList = properMoves.map((move, index) => ({
                move: move.notation,
                content: move.content || '',
                fen: move.fen,
                moveNumber: Math.floor(index / 2) + 1,
                color: index % 2 === 0 ? 'white' : 'black'
              }));
              setMoveList(moveList);
              
              // Store comprehensive moves for retrieval  
              window._lastComprehensiveResult = {
                success: true,
                moves: properMoves,
                gameResult: {
                  result: "unfinished",
                  winner: null,
                  reason: "ongoing"
                },
                finalFEN: lastSuccessfulFen
              };
              
              

              return true;
            } else {
              const errorMsg = "INVALID PGN - NO VALID MOVES FOUND";
              console.error(errorMsg);
              setPgnError(errorMsg);
              setValidationMessage({ text: errorMsg, isError: true });
              return false;
            }
          } else {
            const errorMsg = "INVALID PGN - NO VALID MOVES FOUND";
            console.error(errorMsg);
            setPgnError(errorMsg);
            setValidationMessage({ text: errorMsg, isError: true });
            return false;
          }
        } catch (e) {
          const errorMsg = `INVALID PGN - PARSING ERROR: ${e.message || e}`;
          console.error("Fallback parsing failed with exception:", e);
          setPgnError(errorMsg);
          setValidationMessage({ text: errorMsg, isError: true });
          return false;
        }
      }
    } catch (e) {
      const errorMsg = `INVALID PGN - UNEXPECTED ERROR: ${e.message || e}`;
      console.error("Unexpected error during PGN parsing:", e);
      setPgnError(errorMsg);
      setValidationMessage({ text: errorMsg, isError: true });
      return false;
    }
  };

  const handleSetFen = () => {
    // Only allow setting FEN if not in PGN mode
    if (isPgnMode) return false;

    try {

      setValidationMessage({ text: "", isError: false });

      // Basic validation for empty FEN
      if (!fen.trim()) {
        const errorMsg = "FEN position cannot be empty";
        setFenError(errorMsg);
        setValidationMessage({ text: errorMsg, isError: true });
        return false;
      }

      chess.load(fen);
      const validFen = chess.fen();

      setPosition(validFen);
      setFenError("");

      // Check for other conditions like missing kings
      const fenParts = validFen.split(' ');
      const piecePlacement = fenParts[0];

      if (!piecePlacement.includes('K') || !piecePlacement.includes('k')) {
        const errorMsg = "Invalid position: Both white and black kings must be on the board";
        setFenError(errorMsg);
        setValidationMessage({ text: errorMsg, isError: true });
        return false;
      }

      return true;
    } catch (e) {
      const errorMsg = `Invalid FEN position: ${e.message || "Unknown error"}`;
      console.error("FEN validation failed:", e.message || e);
      setFenError(errorMsg);
      setValidationMessage({ text: errorMsg, isError: true });
      return false;
    }
  };

  const pieces = [
    'wP',
    'wN',
    'wB',
    'wR',
    'wQ',
    'wK',
    'bP',
    'bN',
    'bB',
    'bR',
    'bQ',
    'bK',
  ];
  return (
    <Modal
      open={modalOpen}
      onClose={() => {
        setModalOpen(false);
        onClose && onClose({ skipGameModeChooser: false });
      }}
    >
      <Box style={{ background: "linear-gradient(to top, darkslategray, #3066BE, gold)", border: "5px solid black" }} sx={modalBoxStyle}>


        {/* Main row layout with two columns */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
          {/* Left column - Chessboard and pieces */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <ChessboardDnDProvider>
              {/* Spare pieces top row */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '3px',
                border: '3px solid black',
                width: '400px',
                borderRadius: '5px',
                opacity: isPgnMode ? 0.5 : 1, // Dim spare pieces in PGN mode
              }}>
                {pieces.slice(6, 12).map((piece) => (
                  <SparePiece
                    key={piece}
                    piece={piece}
                    width={400 / 7}
                    dndId="ManualBoardEditor"
                  />
                ))}
              </div>

              {/* Chessboard */}
              <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
                <Chessboard
                  customDarkSquareStyle={{
                    background: 'linear-gradient(to left, #3066BE, #4A7DD9)',
                  }}
                  customLightSquareStyle={{
                    background: 'linear-gradient(to left, #fffff0, #f5e4c3)',
                  }}
                  id="ManualBoardEditor"
                  position={position}
                  boardOrientation={boardOrientation}
                  onSparePieceDrop={isPgnMode ? null : handleSparePieceDrop}
                  onPieceDrop={isPgnMode ? null : handlePieceDrop}
                  boardWidth={400}
                  onPieceDropOffBoard={isPgnMode ? null : handlePieceDropOffBoard}
                  dropOffBoardAction="trash"
                  customBoardStyle={{
                    border: '3px solid black',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    opacity: isPgnMode ? 0.9 : 1, // Slightly dim board in PGN mode
                  }}
                />


              </div>

              {/* Spare pieces bottom row */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '3px',
                border: '3px solid black',
                width: '400px',
                borderRadius: '5px',
                opacity: isPgnMode ? 0.5 : 1, // Dim spare pieces in PGN mode
              }}>
                {pieces.slice(0, 6).map((piece) => (
                  <SparePiece
                    key={piece}
                    piece={piece}
                    width={400 / 7}
                    dndId="ManualBoardEditor"
                  />
                ))}
              </div>
            </ChessboardDnDProvider>
          </div>

          {/* Right column - FEN/PGN inputs and buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            flex: 1,
            padding: '0px 10px'
          }}>
            {/* FEN and PGN inputs */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginBottom: '0px',
              position: 'relative'
            }}><div style={{ position: 'absolute', top: '8px', right: '0px' }}>
                <i
                  className="fa-solid fa-circle-xmark"
                  style={{ fontSize: '24px', color: '#ff0000', cursor: 'pointer' }}
                  onClick={() => {
                    setModalOpen(false);
                    onClose && onClose({
                      skipGameModeChooser: isPgnMode,
                      isPgn: isPgnMode,
                      isFen: !isPgnMode
                    });
                  }}
                ></i>
              </div>

              <div style={{
                fontSize: '30px',
                fontWeight: 'bold',
                textAlign: 'center',
                borderBottom: '2px solid black',
                paddingBottom: '5px',
                color: 'black',
                marginBottom: '5px',
              }}>
                A D V A N C E D&nbsp;&nbsp;&nbsp;&nbsp;S E T U P
                <div style={{ color: 'black', fontSize: '16px', fontWeight: 'normal', fontStyle: 'italic' }}>Make illegal moves, or steal your opponents pieces.</div>

              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '24px', fontWeight: 'bold', width: '', display: 'flex', alignItems: 'center', gap: '5px', color: 'black' }}><i className="fa-solid fa-info-circle" style={{ fontSize: '16px', color: 'black', cursor: 'pointer' }} onClick={() => {
                  window.open('https://en.wikipedia.org/wiki/Portable_Game_Notation#Example', '_blank');
                }}></i> PGN - Import Full Game:</label>
                <textarea
                  style={{
                    flex: 1,
                    padding: '15px',
                    fontSize: '18px',
                    border: '3px solid black',
                    borderRadius: '5px',
                    backgroundColor: isPgnMode ? 'gold' : 'transparent',
                    height: '100px',
                    resize: 'none',
                    color: 'black'
                  }}
                  className="placeholder-black"
                  placeholder="Enter PGN . . ."
                  value={pgnInput}
                  onChange={handlePgnInputChange}
                />

                {/* Game Result Indicator - Only shows when a result is detected */}
                {gameResult.result && gameResult.result !== "unknown" && (
                  <div style={{
                    marginTop: '5px',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    border: '2px solid black',
                    backgroundColor: gameResult.result === 'checkmate' ? '#ffeb3b' :
                      gameResult.result === 'draw' ? '#90caf9' :
                        gameResult.result === 'resigned' ? '#ff9800' : '#e0e0e0',
                    color: 'black'
                  }}>
                    {gameResult.result === 'checkmate' && (
                      <span>Checkmate: {gameResult.winner === 'white' ? 'White' : 'Black'} wins</span>
                    )}
                    {gameResult.result === 'draw' && (
                      <span>Draw: {gameResult.reason === 'stalemate' ? 'Stalemate' :
                        gameResult.reason === 'insufficient material' ? 'Insufficient Material' :
                          gameResult.reason === 'threefold repetition' ? 'Threefold Repetition' :
                            gameResult.reason === 'fifty move rule' ? 'Fifty-move Rule' :
                              'Agreement'}</span>
                    )}
                    {gameResult.result === 'resigned' && (
                      <span>Resignation: {gameResult.winner === 'white' ? 'White' : 'Black'} wins</span>
                    )}
                    {gameResult.result === 'unfinished' && (
                      <span>Unfinished game</span>
                    )}
                  </div>
                )}

                {/* {pgnError && (
                  <div style={{
                    marginTop: '5px',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '16px',
                    backgroundColor: '#ffcdd2',
                    color: '#b71c1c',
                    border: '2px solid #b71c1c'
                  }}>
                    {pgnError}
                  </div>
                )} */}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>

                <label style={{ fontSize: '24px', fontWeight: 'bold', width: '', display: 'flex', alignItems: 'center', gap: '5px' }}><i className="fa-solid fa-info-circle" style={{ fontSize: '16px', color: 'black', cursor: 'pointer' }} onClick={() => {
                  window.open('https://en.wikipedia.org/wiki/Forsyth%E2%80%93Edwards_Notation#Examples', '_blank');
                }}></i> FEN - Custom Start Position:</label>
                <textarea
                  style={{
                    flex: 1,
                    padding: '15px',
                    fontSize: '18px',
                    border: '3px solid black',
                    borderRadius: '5px',
                    backgroundColor: isPgnMode ? 'transparent' : 'gold',
                    height: '100px',
                    resize: 'none',
                    color: 'black',
                    opacity: isPgnMode ? 0.5 : 1, // Dim FEN field when in PGN mode
                  }}
                  className="placeholder-black"
                  placeholder="Enter FEN . . ."
                  value={fen}
                  onChange={handleFenInputChange}
                  disabled={isPgnMode} // Disable FEN field when in PGN mode
                />
              </div>
            </div>
            {/* RESET and EMPTY pair */}
            <div style={{
              display: 'flex',
              gap: '15px',
              width: '100%',
              marginTop: '15px',
            }}>
              <button
                className="FEN-buttons reg-buttons"
                style={{
                  fontSize: '20px',
                  padding: '20px',
                  border: '3px solid black',
                  color: 'black',
                  borderRadius: '5px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1, // Equal width distribution
                  cursor: isPgnMode ? 'not-allowed' : 'pointer',
                  opacity: isPgnMode ? 0.6 : 1, // Dim in PGN mode
                }}
                onClick={() => {
                  if (isPgnMode) return;
                  chess.reset();
                  const newFen = chess.fen();
                  setPosition(newFen);
                  setFen(newFen);
                }}
                disabled={isPgnMode} // Disable in PGN mode
              >
                RESET BOARD
              </button>
              <button
                className="FEN-buttons reg-buttons"
                style={{
                  fontSize: '20px',
                  padding: '20px',
                  border: '3px solid black',
                  color: 'black',
                  borderRadius: '5px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1, // Equal width distribution
                  cursor: isPgnMode ? 'not-allowed' : 'pointer',
                  opacity: isPgnMode ? 0.6 : 1, // Dim in PGN mode
                }}
                onClick={() => {
                  if (isPgnMode) return;
                  chess.clear();
                  const newFen = chess.fen();
                  setPosition(newFen);
                  setFen(newFen);
                }}
                disabled={isPgnMode} // Disable in PGN mode
              >
                CLEAR BOARD
              </button>
            </div>
            {/* Control buttons - Two pairs: RESET/EMPTY and CANCEL/LOAD */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              width: '100%',
              marginTop: '15px',
            }}>
              <button
                style={{
                  padding: '20px',
                  backgroundColor: position.split(' ')[1] === 'w' ? 'white' : 'black',
                  color: position.split(' ')[1] === 'w' ? 'black' : 'white',
                  fontWeight: 'bold',
                  borderRadius: '5px',
                  border: '4px solid black',
                  flex: '1',
                  fontSize: '24px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: isPgnMode ? 'not-allowed' : 'pointer',
                  opacity: isPgnMode ? 0.6 : 1, // Dim in PGN mode
                }}
                onClick={handleFlipBoard}
                disabled={isPgnMode} // Disable in PGN mode
              >
                {position.split(' ')[1] === 'w' ? 'White to Move' : 'Black to Move'}
                <i class="fa-solid fa-retweet" style={{ marginLeft: '10px', fontSize: '24px', color: position.split(' ')[1] === 'w' ? 'black' : 'white' }} ></i>
              </button>


              {/* CANCEL and LOAD POSITION pair */}
              <div style={{
                display: 'flex',
                width: '100%'
              }}>

                <button
                  className="FEN-buttons reg-buttons"
                  style={{
                    fontSize: '24px',
                    padding: '35px 20px',
                    width: '100%',
                    background: 'gold',
                    color: 'black',
                    border: '4px solid black',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                  onClick={() => {


                    // Clear any previous validation messages
                    setValidationMessage({ text: "", isError: false });

                    // Validate based on active mode
                    let isValid = false;

                    if (isPgnMode) {
                      // For PGN: validate and process in one step
                      isValid = handleLoadPgn();

                      // If there's an error stored in state, don't proceed
                      if (pgnError) {
                        setValidationMessage({ text: pgnError, isError: true });
                        isValid = false;
                      }
                    } else {
                      // For FEN: validate position
                      isValid = handleSetFen();

                      // If there's an error stored in state, don't proceed
                      if (fenError) {
                        setValidationMessage({ text: fenError, isError: true });
                        isValid = false;
                      }
                    }

                    // Only proceed if validation passed
                    if (isValid) {

                      setValidationMessage({ text: "Position validated successfully!", isError: false });

                      // Short delay to show success message before closing
                      setTimeout(() => {
                        setModalOpen(false);

                        if (isPgnMode) {
                          // PGN FLOW: Send PGN data and DIRECTLY close without showing GameModeChooser

                          // Get comprehensive moves if available
                          const comprehensiveMoves = window._lastComprehensiveResult?.moves || null;
                          
                          // console.log('[FENBuilderModal] LOAD PGN - DEBUG COMPREHENSIVE MOVES:', {
                          //   hasLastResult: !!window._lastComprehensiveResult,
                          //   comprehensiveMovesLength: comprehensiveMoves?.length || 0,
                          //   comprehensiveMoves: comprehensiveMoves,
                          //   gameResult: gameResult,
                          //   moveListLength: moveList?.length || 0
                          // });

                          // Call onPGNUpdate with the validated PGN data
                          // console.log('[FENBuilderModal] Calling onPGNUpdate with:', {
                          //   pgnLength: pgnInput?.length,
                          //   moveListLength: moveList?.length,
                          //   comprehensiveMovesLength: comprehensiveMoves?.length,
                          //   gameResult: gameResult
                          // });

                          onPGNUpdate(pgnInput, {
                            moveList: moveList.length > 0 ? moveList : undefined,
                            gameResult: gameResult,
                            comprehensiveMoves: comprehensiveMoves // Pass the comprehensive moves
                          });

                          // Close everything (including skipping GameModeChooser)
                          onClose && onClose({ skipGameModeChooser: true, isPgn: true });
                        } else {
                          // FEN FLOW: Send FEN data and show GameModeChooser


                          // Call onFENUpdate with position

                          onFENUpdate(position, gameResult);

                          // Explicitly tell parent to show GameModeChooser
                          onClose && onClose({ skipGameModeChooser: false, isFen: true });
                        }
                      }, 500); // 500ms delay to show success message
                    } else {

                      // Don't close modal, error messages should be displayed
                    }
                  }}
                >
                  {isPgnMode ? 'LOAD PGN' : 'LOAD POSITION'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add a validation message display after the load button */}
        {/* {validationMessage.text && (
          <div style={{
            padding: '10px 15px',
            borderRadius: '5px',
            fontSize: '16px',
            fontWeight: validationMessage.isError ? 'bold' : 'normal',
            backgroundColor: validationMessage.isError ? '#ffcdd2' : '#c8e6c9',
            color: validationMessage.isError ? '#b71c1c' : '#1b5e20',
            border: `2px solid ${validationMessage.isError ? '#b71c1c' : '#1b5e20'}`,
            textAlign: 'center'
          }}>

            {validationMessage.text}
        
          </div>
        )} */}
      </Box>
    </Modal>
  );
};

export default FENBuilderModal;
